'use client';

import { Fragment, ReactNode, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

export interface IAssessmentModalOptions {
  description: TTextLocalized;
  onCancel: () => void;
  onCancelLabel: TTextLocalized;
  onContinue: () => void;
  onContinueLabel: TTextLocalized;
  title: TTextLocalized;
  warning: TTextLocalized;
  warningSubText: TTextLocalized;
}

interface IAssessmentModalProps extends IAssessmentModalOptions {
  /**
   * The component that opens the modal
   */
  renderModalTrigger: ({
    onOpenModal,
  }: {
    onOpenModal: () => void;
  }) => ReactNode;
}

/**
 * -----------------------------------------------------------------------------
 * This will render the content of popup modal for the assessment and will be
 * customizable to be used in different scenarios of the evaluation process.
 */
export function AssessmentModal({
  description,
  onCancel,
  onCancelLabel,
  onContinue,
  onContinueLabel,
  renderModalTrigger,
  title,
  warning,
  warningSubText,
}: IAssessmentModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOnCloseModal = () => {
    setIsOpen(false);
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleOnCancel = () => {
    setIsOpen(false);
    onCancel();
  };

  const handleOnContinue = () => {
    setIsOpen(false);
    onContinue();
  };

  return (
    <>
      {renderModalTrigger({ onOpenModal: handleOpenModal })}

      <Transition appear as={Fragment} show={isOpen}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={handleOnCloseModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div
              className="
                flex min-h-full items-center justify-center p-4 text-center
              "
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="
                    w-full max-w-sm transform overflow-hidden rounded-2xl
                    bg-white p-6 text-start align-middle shadow-xl transition-all
                    rtl:text-end dark:bg-gray-600
                  "
                >
                  <div className="bottom-5 h-auto w-full border-teal-600">
                    <Dialog.Title className="text-3xl font-semibold">
                      <Text text={title} />
                    </Dialog.Title>
                    <Dialog.Description
                      className="
                        mt-2 text-sm leading-4 text-zinc-600 dark:text-zinc-300
                      "
                    >
                      <Text text={description} />
                    </Dialog.Description>

                    <hr className="my-2 h-[1px] border-0 bg-gray-200 dark:bg-gray-400" />

                    <p className="text-lg">
                      <Text text={warning} />
                    </p>

                    <p className="mt-2 text-sm leading-4 text-zinc-600 dark:text-zinc-300">
                      <Text text={warningSubText} />
                    </p>

                    <div className="mt-4 flex w-full justify-between gap-4">
                      <button
                        className="
                          form-btn-secondary border-primary-300 w-auto border
                          bg-white
                        "
                        onClick={handleOnCancel}
                        type="button"
                      >
                        <Text text={onCancelLabel} />
                      </button>

                      <button
                        className="form-btn z-50 w-auto border border-gray-300"
                        onClick={handleOnContinue}
                        type="button"
                      >
                        <Text text={onContinueLabel} />
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
